import { useEffect, useState } from 'react';
import type { Round } from '../data/types';
import { isReal } from '../data/types';

/* Preload every image the round will ever show — each fake graphic and both stamp
   images — BEFORE the first headline appears.

   This is the single most valuable difference from the prototype. There, the graphic was
   fetched at the moment of the reveal, so a slow fetch put a black frame on camera in the
   middle of a take. Fetching everything up front converts that into a wait before recording
   starts, where a wait costs nothing.

   Failures deliberately do not block. A graphic that 404s here will show the branded
   "graphic not found" screen later, which is a far better on-camera outcome than refusing
   to start the round at all.

   ── Endless Mode ──
   Caching everything up front is right for a ten-headline series and wrong for a pool of two
   hundred: Adorian would sit watching a loading bar for the graphics of headlines he may never
   reach. `blockingItems` caps how far AHEAD the gate waits. Everything still starts loading at
   once — the rest simply warms in the background instead of holding the START button hostage. */

export interface PreloadState {
  ready: boolean;
  loaded: number;
  total: number;
  failed: string[];
}

const dedupe = (urls: string[]) => Array.from(new Set(urls.filter(Boolean)));

/** Every image the round could ever show. */
export function imageUrlsFor(round: Round): string[] {
  const urls = round.items.filter((i) => !isReal(i)).flatMap((i) => i.images);
  if (round.stampRealUrl) urls.push(round.stampRealUrl);
  if (round.stampFakeUrl) urls.push(round.stampFakeUrl);
  return dedupe(urls);
}

/**
 * The images START waits on: everything from the first `blockingItems` headlines, plus both
 * stamps (either can be needed on the very first reveal). Undefined means the whole round.
 */
export function blockingUrlsFor(round: Round, blockingItems?: number): string[] {
  if (blockingItems === undefined) return imageUrlsFor(round);
  const head = round.items.slice(0, Math.max(0, blockingItems));
  const urls = head.filter((i) => !isReal(i)).flatMap((i) => i.images);
  if (round.stampRealUrl) urls.push(round.stampRealUrl);
  if (round.stampFakeUrl) urls.push(round.stampFakeUrl);
  return dedupe(urls);
}

/* A request that HANGS fires neither load nor error, so without a deadline the gate waits
   forever and the round can never be started — on a shoot day that is an outage, and a worse
   one than a missing graphic (which has a branded fallback). Start anyway and say what is
   missing. Generous, because it should only ever fire on a genuinely stuck request. */
export const PRELOAD_DEADLINE_MS = 15000;

export function usePreloadRound(round: Round | null, blockingItems?: number): PreloadState {
  const [state, setState] = useState<PreloadState>({ ready: false, loaded: 0, total: 0, failed: [] });

  useEffect(() => {
    if (!round) {
      setState({ ready: false, loaded: 0, total: 0, failed: [] });
      return;
    }

    /* Two sets. `urls` is everything and is what actually gets fetched; `blocking` is the
       subset START waits on. Counts reported to the gate are the blocking ones, so the bar
       fills to the thing it is genuinely waiting for rather than to a number that includes
       images nobody is being held up by. */
    const urls = imageUrlsFor(round);
    const blocking = new Set(blockingUrlsFor(round, blockingItems));
    if (blocking.size === 0) {
      // Nothing to wait for — but still warm the rest for later.
      urls.forEach((url) => { new Image().src = url; });
      setState({ ready: true, loaded: 0, total: 0, failed: [] });
      return;
    }

    let cancelled = false;
    let done = 0;
    const failed: string[] = [];
    const pending = new Set(blocking);

    const settle = (url: string, ok: boolean) => {
      if (cancelled || !pending.has(url)) return;
      pending.delete(url);
      done += 1;
      if (!ok) failed.push(url);
      setState({ ready: done === blocking.size, loaded: done, total: blocking.size, failed: failed.slice() });
    };

    setState({ ready: false, loaded: 0, total: blocking.size, failed: [] });

    const images = urls.map((url) => {
      const img = new Image();
      img.onload = () => settle(url, true);
      img.onerror = () => settle(url, false);
      img.src = url;
      return img;
    });

    const deadline = window.setTimeout(() => {
      if (cancelled || pending.size === 0) return;
      const stuck = [...pending];
      stuck.forEach((url) => pending.delete(url));
      failed.push(...stuck);
      setState({ ready: true, loaded: blocking.size - stuck.length, total: blocking.size, failed: failed.slice() });
    }, PRELOAD_DEADLINE_MS);

    return () => {
      cancelled = true;
      window.clearTimeout(deadline);
      // Drop the handlers so a late load can't call setState after unmount.
      images.forEach((img) => {
        img.onload = null;
        img.onerror = null;
      });
    };
  }, [round, blockingItems]);

  return state;
}
